import React, { useState, useEffect, useContext, forwardRef, useImperativeHandle } from "react";
import ReloadButton from "../../../mandatoryConfigs/uiThemeConfig/widgets/reloadButton";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import {
  getMustDoSavedConfig,
  saveConfiguration,
  getSelectedMustDoActionArrayFromObject,
  populateSavedConfigWithDefaultData,
  checkForDataFieldsValidation,
  convertStateToConfigStructure,
} from "../mustDoActionService";
import { Button, Typography } from "@mui/material";
import { Loader } from "../../../../../components/loader/Loader";
import "./mustDoActionConfig.css";
import "../../mandatoryConfigCommon.css";
import { SingleMustDoDataPointState, SelectedMustDoActionTask, InputLengthValidations } from "../types";

import MustDoActionTaskItem from "./MustDoActionTaskItem";
import { Popup } from "semantic-ui-react";
import { getLob } from "../../../../../services/authenticationService";
import { convertValuesToConfig } from "../../../../../services/uiConfigService";
import { PlaygroundConfigValueContext } from "../../../../../components/newPortal/NewPortalLayout";
import { configurationAttributeType } from "../../../../../types";


interface MustDoActionConfigProps {

  inputLengthValidations: InputLengthValidations;
  triggerReset:number;
  handleReset:()=>void;
}

const MustDoActionConfig= forwardRef<any, MustDoActionConfigProps>  ( ({ inputLengthValidations ,triggerReset,handleReset},ref) =>  {
  // States ->
  const [areDataFieldsValid, setAreDataFieldValid] = useState<boolean>(true);
  const [selectedMustDoAction, setSelectedMustDoAction] = useState<
    Record<string, SingleMustDoDataPointState[]>
  >({});
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedMustDoActionTasks, setSelectedMustDoActionTasks] = useState<
    SelectedMustDoActionTask[]
  >([]);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [hasActiveChild, setHasActiveChild] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);

  const PlaygroundConfigContext = useContext(PlaygroundConfigValueContext);
  const {
    playgroundConfiguration,
    setPlayGroundConfiguration,
    onTriggerConfigChanges,
    keyValue,
    setKeyValue,
    cleanSyncDataLoaderIds,
    setCleanSyncDataLoaderIds,
  } = PlaygroundConfigContext ?? {
    playgroundConfiguration: [],
    setPlayGroundConfiguration: () => {},
    onTriggerConfigChanges: () => {},
    setKeyValue: () => {},
    keyValue: 0,
    cleanSyncDataLoaderIds: [],
    setCleanSyncDataLoaderIds: () => {},
  };

  useEffect(() => {
    // Check if `activeChild` exists in localStorage when the component mounts
    setHasActiveChild(!!localStorage.getItem("activeChild"));
  }, []);

  // Event Handlers ->

  // const handleToggleSelect = (taskId: string) => {
  //   if (taskId == expandedTaskId) {
  //     setExpandedTaskId(null);
  //   }
  //   setSelectedMustDoActionTasks((prevTasks) =>
  //     prevTasks.map((task) =>
  //       task.id === taskId ? { ...task, selected: !task.selected } : task
  //     )
  //   );
  // };
  const handleToggleSelect = (taskId: string) => {
    // Toggle selection of the clicked task
    setSelectedMustDoActionTasks((prevTasks) => {
      const updatedTasks = prevTasks.map((task) =>
        task.id === taskId ? { ...task, selected: !task.selected } : task
      );

      // Sort tasks to move selected tasks to the top
      return updatedTasks.sort(
        (a, b) => (b.selected ? 1 : 0) - (a.selected ? 1 : 0)
      );
    });

    // Expand or collapse the task detail
    if (taskId === expandedTaskId) {
      setExpandedTaskId(null);
    }
  };

  const handleToggleExpand = (taskId: string) => {
    if (!isDragging) {
      // Prevent toggling if dragging
      setExpandedTaskId((prev) => (prev === taskId ? null : taskId));
    }
  };
  const handleSave = async () => {
    if (!areDataFieldsValid) return;
    const selectedItems = selectedMustDoActionTasks.filter(
      (task) => task.selected
    );
    const selectedMustDoActionNew: Record<
      string,
      SingleMustDoDataPointState[]
    > = {};
    selectedItems.forEach((item) => {
      selectedMustDoActionNew[item.actionId] =
        selectedMustDoAction[item.actionId];
    });
    await saveConfiguration(selectedMustDoActionNew);
  };
  const handleMustDoEditorChange = (
    id: string,
    newValues: Array<SingleMustDoDataPointState>
  ) => {
    setSelectedMustDoAction((pre) => ({
      ...pre,
      [id]: newValues,
    }));
  };
  const reorder = (
    list: SelectedMustDoActionTask[],
    startIndex: number,
    endIndex: number
  ): SelectedMustDoActionTask[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };
  const onDragStart = () => setIsDragging(true);
  const onDragEnd = (result: DropResult) => {
    setIsDragging(false);
    if (!result.destination) return;

    const reorderedTasks = reorder(
      selectedMustDoActionTasks,
      result.source.index,
      result.destination.index
    );

    setSelectedMustDoActionTasks(reorderedTasks);
  };

  // Initializing the component's data variables ->
  const fetchData = async () => {
    try {
      /* flow
       * 1. get the saved config from backend , i.e if a field is absent its data wont be available
       * 2. convert the savedConfig to a custom taskArray for select unsalect and order of items logic
       * 3. populateSavedConfig obejct with default data , if some items were absent , since we are using this varibable to get the data when rendering input fields
       * 4. setup state variables
       */
      setLoading(true);
      // ;
      const savedConfig = await getMustDoSavedConfig();
      const tasksArray = getSelectedMustDoActionArrayFromObject(savedConfig);

      const populatedSavedConfig =
        populateSavedConfigWithDefaultData(savedConfig);
      setSelectedMustDoAction(populatedSavedConfig);
      // if none of the tasks array item's selected key is true , means that user came for the first time , so then make the selected true for all items of task array
      // Check if any tasks are selected
      const anyTaskSelected = tasksArray.some((task) => task.selected);
      if (!anyTaskSelected) {
        // If none are selected, update all tasks to selected: true
        const updatedTasksArray = tasksArray.map((task) => ({
          ...task,
          selected: true,
        }));
        setSelectedMustDoActionTasks(updatedTasksArray);
      } else {
        setSelectedMustDoActionTasks(tasksArray);
      }
    } catch (error) {
      console.error("Error loading configuration:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // console.log("triggerReset",triggerReset)
    fetchData();
  }, []);
  useEffect(()=>{
    // console.log("triggerReset",triggerReset)
    fetchData();
    setExpandedTaskId(null);
  },[triggerReset])
  useEffect(() => {
    setAreDataFieldValid(
      checkForDataFieldsValidation(
        selectedMustDoActionTasks,
        selectedMustDoAction,
        inputLengthValidations
      )
    );

    handlePlaygroudnChanges();
  }, [selectedMustDoActionTasks, selectedMustDoAction]);

  async function handlePlaygroudnChanges() {
    const selectedItems = selectedMustDoActionTasks.filter(
      (task) => task.selected
    );
    const selectedMustDoActionNew: Record<
      string,
      SingleMustDoDataPointState[]
    > = {};
    selectedItems.forEach((item) => {
      selectedMustDoActionNew[item.actionId] =
        selectedMustDoAction[item.actionId];
    });
    let configVal = await convertStateToConfigStructure(
      selectedMustDoActionNew
    );

    const convertedVal: any = configVal.map((obj) => ({
      ...obj,
      isValueUpdated: true,
      domainType: "salesrep_must_do_action_configuration",
    }));
    setCleanSyncDataLoaderIds([]);
    setPlayGroundConfiguration(convertedVal);
  }
  const  handlePreview= async() => {
            try {
              setIsSpinning(true);

              const res: any = await onTriggerConfigChanges(); // Wait for the API call to complete
              //  alert(res)
              // console.log("res is " + res);
              if (res["status"] === 200 || res["status"] === 200) {
                setKeyValue(keyValue + 1);
              }
            } catch (error) {
              console.error("Error in onTriggerConfigChanges:", error);
            }
            setTimeout(() => {
              setIsSpinning(false);
            }, 1000);
          }
         
         useImperativeHandle(ref,()=>(
          {handlePreview,
          validationFailiure:!areDataFieldsValid,
          }
          
         ))

  return (
    <div className="config-main-logic-container">
      <div className="config-main">
        {loading ? (
          <Loader />
        ) : (
          <>
          <div style={{padding:"8px",backgroundColor:"#f8fafc"}} >
            <Typography
              sx={{
                color: "#000000",
                fontSize: { xs: "1rem", md: "1.2rem", xl: "1.5rem" },
                fontWeight: "500",
              }}
             
            >
              Manage Must Do Action
            </Typography>
             </div>

            <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="configuration-items-container "
                    style={{padding:"6px"}}
                  >
                    {selectedMustDoActionTasks.map((task, index) => (
                      <MustDoActionTaskItem
                        key={task.id}
                        task={task}
                        index={index}
                        expandedTaskId={expandedTaskId}
                        handleToggleSelect={handleToggleSelect}
                        handleToggleExpand={handleToggleExpand}
                        handleMustDoEditorChange={handleMustDoEditorChange}
                        selectedMustDoAction={selectedMustDoAction}
                        inputLengthValidations = {inputLengthValidations}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
      </div>
      <div
        className={`config-save-button-container ${
          !areDataFieldsValid ? "custom-cursor-not-allowed" : ""
        }  ${hasActiveChild ? "has-active-child" : ""}`}
      >
         <Button
                disabled={!areDataFieldsValid}
               
                onClick={handleReset}
                sx={{
                  alignSelf: "flex-end",
                  fontSize: "13px",
                  color:"grey",
                  padding:"6px 24px",
                  border:"0.5px solid grey",
                  marginRight:"10px",
                  textTransform:"none",
                  borderRadius:"8px",
                }}
              >
                Reset
               
              </Button>
              <Button
                disabled={!areDataFieldsValid}
               
                onClick={handleSave}
                sx={{
                  alignSelf: "flex-end",
                  borderRadius:"8px",
                  padding:"6px 24px",
                  fontSize: "13px",
                  border:"0.5px solid #00C6B1",
                   backgroundColor: "#00C6B1",
                  color:"#ffffff",
                   "&:hover": {
              backgroundColor: "#00C6B1", 
            },
            "&:active": {
              backgroundColor: "#00C6B1",
            },
                  textTransform:"none"
                }}
              >
                Save
              </Button>
        {/* <Button
        disabled = {!areDataFieldsValid}
          variant="contained"
          // color= "#00C6B1"
        onClick={handlePreview}
         
          sx={{
            alignSelf: "flex-end",
            marginY: "1rem",
            fontSize: "clamp(0.5rem,0.9vw + 0.065rem, 1.6rem)",
            backgroundColor: "#00C6B1",
            marginRight: "10px",
          }}
        >
          PREVIEW
          {/* <div sx={{color:""}}> */}
          {/* <ReloadButton
            isSpinning={isSpinning}
            setIsSpinning={setIsSpinning} */}
            {/* // handleClick={async () =>{ */}
               {/* await onTriggerConfigChanges();
               setRefreshCount((prev) => prev + 1);
             }}
            handleClick={async () => {
              try {
                setIsSpinning(true);

                const res: any = await onTriggerConfigChanges(); // Wait for the API call to complete
                //  alert(res)
                // console.log("res is " + res);
                if (res["status"] === 200 || res["status"] === 200) {
                  setKeyValue(keyValue + 1);
                }
              } catch (error) {
                console.error("Error in onTriggerConfigChanges:", error);
              }
              setTimeout(() => {
                setIsSpinning(false);
              }, 1000);
            }}
          /> */}
          {/* </div> */}
          {/* </div> */}
        {/* </Button> * */}
        {/* <Button
        disabled = {!areDataFieldsValid}
          variant="contained"
          onClick={handleSave}
          sx={{
            alignSelf: "flex-end",
            marginY: "1rem",
            fontSize: "clamp(0.5rem,0.9vw + 0.065rem, 1.6rem)",
            gap:"7px"
          }}
        >
          SAVE
          <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" height="1.2em" width="1.2em" xmlns="http://www.w3.org/2000/svg"><path d="M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l370.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128z"></path></svg>
        </Button> */}
      </div>
    </div>
  );
})

export default MustDoActionConfig;
